// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Share & Earn  (route /share-earn — "Share & Earn" row on the profile page)
// The customer's referral screen. It shows their real code with Copy, share buttons (WhatsApp, Telegram, SMS, copy link, and the
// device's share sheet where there is one), their referral numbers (invited / in progress / completed and — only when a reward
// is switched on — total earned) and, most importantly, the friends who joined with their code and how far each has got:
// Joined → Ordered → Completed (their order was delivered), each with a date. Friends appear as first name + last initial only.
// The reward is whatever the backend says (`rewardInr`, from REFERRAL_REWARD_INR): when it is 0 the screen says it only tracks
// referrals and shows no ₹ amounts, so it never promises money that will not arrive; when it is on, the wallet is credited once,
// when the friend's first order is delivered (see docs/FEATURES.md §2.13). Signed-out visitors go to /welcome.
// Deliberately NOT here (nothing supports it): "Give ₹100, get ₹100" to the friend (no friend-side offer exists beyond the welcome
// coupon every account gets), "Harbor Master" milestones / bonuses, a QR code, credit expiry ("valid for 180 days"), the friend's
// order contents.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, CircleCheck, Clock, Copy, Gift, Link2, MessageCircle, PackageCheck, Send, Share2, Smartphone, Wallet } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageTransition from "../components/PageTransition";
import useAuth from "../hooks/useAuth";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { formatWhen } from "../utils/orders";
import { formatInr } from "../utils/priceFormat";
import { fetchReferrals, inviteMessage, referralLink, type ReferralSummary, type ReferredFriend } from "../utils/referrals";
import { fetchWallet } from "../utils/wallet";

type Load = { status: "loading" } | { status: "error" } | { status: "ready"; data: ReferralSummary };

const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

function StatusBadge({ friend, reward }: { friend: ReferredFriend; reward: number }) {
  const base = "inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold";
  if (friend.status === "completed") {
    return (
      <span className={`${base} bg-teal-100 text-teal-700`}>
        <CircleCheck className="h-3.5 w-3.5" aria-hidden="true" /> {friend.rewardInr > 0 ? `+${formatInr(friend.rewardInr)} credited` : reward > 0 ? "Completed" : "Order delivered"}
      </span>
    );
  }
  if (friend.status === "ordered") {
    return (
      <span className={`${base} bg-ocean-700/10 text-ocean-900 dark:bg-white/10 dark:text-foam-50`}>
        <PackageCheck className="h-3.5 w-3.5" aria-hidden="true" /> Ordered
      </span>
    );
  }
  return (
    <span className={`${base} bg-ocean-700/5 text-ocean-800 dark:bg-white/5 dark:text-foam-100`}>
      <Clock className="h-3.5 w-3.5" aria-hidden="true" /> Joined
    </span>
  );
}

const detail = (friend: ReferredFriend) =>
  friend.status === "completed" && friend.completedAt
    ? `First order delivered ${formatWhen(friend.completedAt)}`
    : friend.status === "ordered" && friend.orderedAt
      ? `First order placed ${formatWhen(friend.orderedAt)}`
      : `Signed up ${formatWhen(friend.joinedAt)}`;

export default function ShareEarn() {
  useDocumentTitle("Share & Earn");
  const auth = useAuth();
  const signedIn = auth.status === "signed-in";
  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [balance, setBalance] = useState<number | null>(null);
  const [copied, setCopied] = useState<"" | "code" | "link">("");

  const refresh = () =>
    fetchReferrals().then(
      (data) => setLoad({ status: "ready", data }),
      () => setLoad((current) => (current.status === "ready" ? current : { status: "error" })),
    );

  useEffect(() => {
    if (!signedIn) return;
    void refresh();
    fetchWallet().then((wallet) => setBalance(wallet.balance), () => undefined);
  }, [signedIn]);

  if (auth.status === "signed-out") return <Navigate to="/welcome" replace />;

  const data = load.status === "ready" ? load.data : null;
  const reward = data?.rewardInr ?? 0;

  const copy = async (what: "code" | "link") => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(what === "code" ? data.code : referralLink(data.code));
      setCopied(what);
      window.setTimeout(() => setCopied(""), 2000);
    } catch {
      // Clipboard refused: the code is on screen.
    }
  };
  const nativeShare = () => {
    if (data) void navigator.share({ title: "ZenFresh", text: inviteMessage(data.code) }).catch(() => undefined);
  };

  const card = "rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10";
  const tile = "flex min-h-16 flex-col items-center justify-center gap-1 rounded-xl bg-ocean-700/5 px-2 py-3 text-xs font-bold text-ocean-900 hover:bg-ocean-700/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 dark:bg-white/10 dark:text-foam-50";

  return (
    <PageTransition>
      <div className="bg-foam-50 pb-24 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs />
        <div className="mx-auto flex max-w-xl flex-col gap-4 px-4 pt-2">
          <Link to="/coming-soon/harbor-rewards" className="flex min-h-12 items-center justify-between gap-3 rounded-2xl bg-coral-500/10 px-4 py-3 text-sm font-bold text-ocean-950 ring-1 ring-coral-500/30 dark:text-foam-50">
            <span>Rewards, milestones &amp; eco-box credit</span>
            <span className="shrink-0 rounded-full bg-coral-500/20 px-2 py-0.5 text-[10px] text-coral-600 dark:text-coral-300">Coming soon</span>
          </Link>
          <header className="flex items-center gap-3">
            <Link to="/profile" aria-label="Back to my profile" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10">
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-2xl font-bold text-ocean-950 dark:text-foam-50">Share &amp; Earn</h1>
              <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">Refer friends, see who joined</p>
            </div>
            {balance !== null && (
              <Link to="/wallet" aria-label={`Wallet, ${formatInr(balance)}`} className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-white px-3 text-sm font-bold text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-50 dark:ring-white/10">
                <Wallet className="h-4 w-4" aria-hidden="true" /> {formatInr(balance)}
              </Link>
            )}
          </header>

          <section aria-label="About the programme" className="rounded-2xl bg-ocean-800 p-5 text-white shadow-sm">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-bold"><Gift className="h-3.5 w-3.5" aria-hidden="true" /> Refer a friend</span>
            <h2 className="mt-3 font-display text-2xl font-bold leading-snug">
              {reward > 0 ? `Refer a friend, earn ${formatInr(reward)}` : "Bring your friends to ZenFresh"}
            </h2>
            <p className="mt-1 text-sm text-foam-100/90">
              {reward > 0
                ? `When a friend joins with your code and their first order is delivered, ${formatInr(reward)} is credited to your ZenFresh wallet.`
                : "Share your code. Friends who join with it show up below, and you can see when they place their first order."}
            </p>
            {reward > 0 && <p className="mt-3 border-t border-white/15 pt-3 text-xs text-foam-100/90">Credited to your wallet on delivery · no limit on referrals</p>}
          </section>

          {load.status === "loading" && <p role="status" className={`${card} text-center text-sm`}>Loading your referrals…</p>}
          {load.status === "error" && (
            <div role="alert" className={`${card} text-center`}>
              <p className="font-display text-lg font-bold">We couldn&apos;t load your referrals</p>
              <button type="button" onClick={() => void refresh()} className="mt-3 min-h-11 rounded-full bg-ocean-800 px-6 text-sm font-bold text-white hover:bg-ocean-700">Try again</button>
            </div>
          )}

          {data && (
            <>
              <section aria-labelledby="code-heading" className={`${card} space-y-4`}>
                <div>
                  <h2 id="code-heading" className="text-xs font-bold uppercase tracking-wide text-ocean-800/80 dark:text-foam-100/75">Your referral code</h2>
                  <div className="mt-1.5 flex items-center justify-between gap-2 rounded-xl bg-ocean-700/5 px-4 py-2 dark:bg-white/5">
                    <span className="font-display text-2xl font-bold tracking-wider text-ocean-950 dark:text-foam-50">{data.code}</span>
                    <button type="button" onClick={() => void copy("code")} className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-ocean-800 px-4 text-sm font-bold text-white hover:bg-ocean-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600">
                      <Copy className="h-4 w-4" aria-hidden="true" /> {copied === "code" ? "Copied" : "Copy code"}
                    </button>
                  </div>
                </div>
                <a href={`https://wa.me/?text=${encodeURIComponent(inviteMessage(data.code))}`} target="_blank" rel="noopener noreferrer" className="flex min-h-12 items-center justify-center gap-2 rounded-full bg-whatsapp px-6 text-sm font-bold text-ocean-950 shadow-sm hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600">
                  <MessageCircle className="h-5 w-5" aria-hidden="true" /> Share via WhatsApp
                </a>
                <div className="grid grid-cols-4 gap-2">
                  <button type="button" onClick={() => void copy("link")} className={tile}><Link2 className="h-5 w-5" aria-hidden="true" />{copied === "link" ? "Copied" : "Copy link"}</button>
                  <a href={`https://t.me/share/url?url=${encodeURIComponent(referralLink(data.code))}&text=${encodeURIComponent(inviteMessage(data.code))}`} target="_blank" rel="noopener noreferrer" className={tile}><Send className="h-5 w-5" aria-hidden="true" />Telegram</a>
                  <a href={`sms:?&body=${encodeURIComponent(inviteMessage(data.code))}`} className={tile}><Smartphone className="h-5 w-5" aria-hidden="true" />SMS</a>
                  {typeof navigator.share === "function" ? (
                    <button type="button" onClick={nativeShare} className={tile}><Share2 className="h-5 w-5" aria-hidden="true" />More…</button>
                  ) : (
                    <span aria-hidden="true" />
                  )}
                </div>
              </section>

              <section aria-labelledby="earn-heading" className={card}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 id="earn-heading" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">{reward > 0 ? "Your referral earnings" : "Your referrals"}</h2>
                    <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">Friends who joined with your code</p>
                  </div>
                  {reward > 0 && (
                    <div className="text-right">
                      <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">Total earned</p>
                      <p className="font-display text-2xl font-bold text-teal-700 dark:text-teal-300">{formatInr(data.totalEarnedInr)}</p>
                    </div>
                  )}
                </div>
                <dl className="mt-3 grid grid-cols-3 gap-2 text-center">
                  {[
                    { label: "Invited", value: data.invited },
                    { label: "Completed", value: data.completed },
                    { label: "In progress", value: data.inProgress },
                  ].map((stat) => (
                    <div key={stat.label} className="rounded-xl bg-ocean-700/5 px-2 py-3 dark:bg-white/5">
                      <dd className="font-display text-2xl font-bold text-ocean-950 dark:text-foam-50">{stat.value}</dd>
                      <dt className="text-xs text-ocean-800/80 dark:text-foam-100/75">{stat.label}</dt>
                    </div>
                  ))}
                </dl>
              </section>

              <section aria-labelledby="how-heading" className={card}>
                <h2 id="how-heading" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">How it works</h2>
                <ol className="mt-3 space-y-3">
                  {[
                    ["Share your code or link", "Send it to friends and family who love fresh seafood."],
                    ["They sign up with it", "Opening your link fills the code in for them at sign-up. They can also type it in."],
                    reward > 0
                      ? [`You earn ${formatInr(reward)}`, "It lands in your ZenFresh wallet once, when their first order has been delivered."]
                      : ["You see how they get on", "They appear below as they join, order and get their first delivery."],
                  ].map(([title, text], i) => (
                    <li key={title} className="flex items-start gap-3">
                      <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ocean-700/10 text-sm font-bold text-ocean-900 dark:bg-white/10 dark:text-foam-50">{i + 1}</span>
                      <span>
                        <span className="block font-semibold text-ocean-950 dark:text-foam-50">{title}</span>
                        <span className="block text-sm text-ocean-800/80 dark:text-foam-100/75">{text}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              </section>

              <section aria-labelledby="activity-heading" className={card}>
                <h2 id="activity-heading" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Friends who joined</h2>
                {data.referrals.length === 0 ? (
                  <p className="mt-3 rounded-xl bg-ocean-700/5 p-4 text-center text-sm text-ocean-800/85 dark:bg-white/5 dark:text-foam-100/80">
                    Nobody has joined with your code yet. Share it above and they will appear here.
                  </p>
                ) : (
                  <ul className="mt-2 divide-y divide-ocean-900/5 dark:divide-white/10">
                    {data.referrals.map((friend, i) => (
                      <li key={`${friend.name}-${friend.joinedAt}-${i}`} className="flex items-center gap-3 py-3">
                        <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ocean-700/10 text-sm font-bold text-ocean-900 dark:bg-white/10 dark:text-foam-50">{initials(friend.name)}</span>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-ocean-950 dark:text-foam-50">{friend.name}</p>
                          <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{detail(friend)}</p>
                        </div>
                        <StatusBadge friend={friend} reward={reward} />
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <p className="rounded-2xl bg-ocean-700/5 p-4 text-center text-xs text-ocean-800/85 dark:bg-white/5 dark:text-foam-100/80">
                Friends are shown by first name and last initial only. Wallet credit is for ZenFresh orders and can&apos;t be withdrawn or transferred. <Link to="/wallet" className="font-semibold underline">Open my wallet</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </PageTransition>
  );
}
